import { FormEvent, useState } from 'react';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import { loadReference } from '../../lib/reference';
import { Button } from '../ui';
import { ConfirmButton, inputClass, Pill, SectionHeader, useLoad } from './kit';

interface Named {
  id: string;
  name: string;
  members?: number;
}
interface Code {
  id: string;
  code: string;
  label: string;
  active: boolean;
}

/** Inline list with add, rename and (optionally) delete. */
function NameList({
  title,
  lead,
  path,
  noun,
  canDelete,
}: {
  title: string;
  lead: string;
  path: string;
  noun: string;
  canDelete: boolean;
}) {
  const [items, reload] = useLoad<Named[]>(path);
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  async function add(e: FormEvent) {
    e.preventDefault();
    const ok = await act(() => api('POST', path, { name: name.trim() }), `${name.trim()} added`);
    if (ok) {
      setName('');
      reload();
      void loadReference();
    }
  }
  async function rename(id: string) {
    const ok = await act(() => api('PATCH', `${path}/${id}`, { name: draft.trim() }));
    if (ok) {
      setEditing(null);
      reload();
    }
  }

  return (
    <section className="rounded-xl border border-line bg-hull/60 p-5">
      <h2 className="font-semibold">{title}</h2>
      <p className="mb-4 mt-1 text-sm text-ink-2">{lead}</p>
      <form onSubmit={add} className="mb-4 flex gap-2">
        <label className="sr-only" htmlFor={`add-${path}`}>
          New {noun}
        </label>
        <input id={`add-${path}`} className={inputClass} placeholder={`New ${noun}`} value={name} onChange={(e) => setName(e.target.value)} />
        <Button tone="primary" type="submit" className="shrink-0 px-3.5 py-2" disabled={name.trim().length < 2}>
          Add
        </Button>
      </form>
      <ul className="divide-y divide-line/60">
        {(items ?? []).map((it) => (
          <li key={it.id} className="flex items-center gap-2 py-2">
            {editing === it.id ? (
              <>
                <input autoFocus className={inputClass} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void rename(it.id)} />
                <Button className="px-3 py-2" onClick={() => void rename(it.id)} disabled={draft.trim().length < 2}>
                  Save
                </Button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm">
                  {it.name}
                  {it.members !== undefined ? <span className="ml-2 text-xs text-ink-3">{it.members} people</span> : null}
                </span>
                <Button
                  tone="ghost"
                  className="px-3 py-2"
                  onClick={() => {
                    setEditing(it.id);
                    setDraft(it.name);
                  }}
                >
                  Rename
                </Button>
                {canDelete ? (
                  <ConfirmButton
                    confirmText="Delete"
                    onConfirm={async () => {
                      const ok = await act(() => api('DELETE', `${path}/${it.id}`).then(() => true), `${it.name} deleted`);
                      if (ok) reload();
                    }}
                  >
                    Delete
                  </ConfirmButton>
                ) : null}
              </>
            )}
          </li>
        ))}
        {items?.length === 0 ? <li className="py-2 text-sm text-ink-3">None yet.</li> : null}
      </ul>
    </section>
  );
}

export function SkillsTeams() {
  return (
    <>
      <SectionHeader title="Skills and teams" lead="Skills match agents to queues. Teams group people for supervisors and reports." />
      <div className="grid gap-4 lg:grid-cols-2">
        <NameList title="Skills" lead="For example a language or a product. Give agents a level per skill under Users." path="/skills" noun="skill" canDelete />
        <NameList title="Teams" lead="Each user can belong to one team." path="/teams" noun="team" canDelete={false} />
      </div>
    </>
  );
}

function CodeList({ title, lead, kind, items, reload }: { title: string; lead: string; kind: 'dispositions' | 'break-reasons'; items: Code[]; reload: () => void }) {
  const [label, setLabel] = useState('');
  const code = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 30);

  async function add(e: FormEvent) {
    e.preventDefault();
    const ok = await act(() => api('POST', `/${kind}`, { code, label: label.trim() }), `${label.trim()} added`);
    if (ok) {
      setLabel('');
      reload();
      void loadReference();
    }
  }
  async function toggle(c: Code) {
    const ok = await act(() => api('PATCH', `/${kind}/${c.id}`, { active: !c.active }));
    if (ok) {
      reload();
      void loadReference();
    }
  }

  return (
    <section className="rounded-xl border border-line bg-hull/60 p-5">
      <h2 className="font-semibold">{title}</h2>
      <p className="mb-4 mt-1 text-sm text-ink-2">{lead}</p>
      <form onSubmit={add} className="mb-4 flex gap-2">
        <label className="sr-only" htmlFor={`add-${kind}`}>
          New {title.toLowerCase()}
        </label>
        <input id={`add-${kind}`} className={inputClass} placeholder="Label agents will see" value={label} onChange={(e) => setLabel(e.target.value)} />
        <Button tone="primary" type="submit" className="shrink-0 px-3.5 py-2" disabled={code.length < 2}>
          Add
        </Button>
      </form>
      <ul className="divide-y divide-line/60">
        {items.map((c) => (
          <li key={c.id} className={`flex items-center gap-3 py-2 ${c.active ? '' : 'opacity-55'}`}>
            <span className="flex-1 text-sm">
              {c.label} <span className="ml-1 text-xs text-ink-3">{c.code}</span>
            </span>
            {c.active ? <Pill tone="good">In use</Pill> : <Pill>Hidden</Pill>}
            <Button tone="ghost" className="px-3 py-2" onClick={() => void toggle(c)}>
              {c.active ? 'Hide' : 'Show'}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function Codes() {
  const [codes, reload] = useLoad<{ dispositions: Code[]; breakReasons: Code[] }>('/admin/codes');
  return (
    <>
      <SectionHeader
        title="Outcomes and breaks"
        lead="Outcomes are what agents pick when they wrap up a call; breaks are the reasons they can step away. Hidden items stay in old reports but are not offered any more."
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <CodeList title="Call outcomes" lead="Shown in wrap-up." kind="dispositions" items={codes?.dispositions ?? []} reload={reload} />
        <CodeList title="Break reasons" lead="Shown in the status menu." kind="break-reasons" items={codes?.breakReasons ?? []} reload={reload} />
      </div>
    </>
  );
}
